import {
  VALIDATE_EMAIL_TYPE,
  VALIDATE_GOOGLE_AUTOCOMPLETE_ADDRESS_TYPE,
  VALIDATE_TEXT_TYPE,
} from './constants/inputValidateTypes';
import validateEmail from '../../shared/helpers/validateEmail';

export default class InputValidityManager {
  private inputValidateAttribute = 'data-input-validate';

  constructor(context: Document | HTMLElement = document) {
    context
      .querySelectorAll(`[${this.inputValidateAttribute}]`)
      .forEach(InputValidityManager.startInputValidity);
  }

  static startInputValidity(inputElement: HTMLInputElement) {
    const { inputValidate } = inputElement.dataset;
    const containerElement =
      inputElement.closest<HTMLElement>('.c-form__group');

    if (!containerElement)
      throw new Error('This input must be inside a form group.');

    InputValidityManager.listenValidity(inputElement);

    switch (inputValidate) {
      case VALIDATE_EMAIL_TYPE:
        InputValidityManager.startInputEmailValidity(inputElement);
        break;
      case VALIDATE_GOOGLE_AUTOCOMPLETE_ADDRESS_TYPE:
        InputValidityManager.startInputGoogleAutocompleteAddressValidity(
          inputElement
        );
        break;
      case VALIDATE_TEXT_TYPE:
      default:
        InputValidityManager.startInputTextValidity(inputElement);
    }
  }

  static listenValidity(inputElement: HTMLInputElement) {
    const containerElement =
      inputElement.closest<HTMLElement>('.c-form__group');
    if (!containerElement)
      throw new Error('This input must be inside a form group.');

    const inputHeight = inputElement.offsetHeight;
    if (inputHeight > 0) {
      containerElement.style.setProperty('--_input-height', `${inputHeight}px`);
    }

    inputElement.addEventListener('validity:isValid', () => {
      containerElement.classList.add('is-valid');
    });

    inputElement.addEventListener('validity:isInvalid', () => {
      containerElement.classList.remove('is-valid');
    });
  }

  static dispatchValidity<T>(
    inputElement: HTMLInputElement,
    isValid: boolean,
    detail?: T
  ) {
    const eventName = isValid ? 'validity:isValid' : 'validity:isInvalid';
    inputElement.dispatchEvent(
      new CustomEvent(eventName, { bubbles: true, detail })
    );
  }

  private static checkValue(
    inputElement: HTMLInputElement,
    checkCondition: (
      value: string,
      e?: CustomEvent
    ) => boolean | Promise<boolean>,
    listenOn = ['input']
  ) {
    const validateValue = async (e?: CustomEvent) => {
      const { value } = inputElement;
      let isValid = value ? inputElement.checkValidity() : true;
      if (isValid) {
        isValid = await checkCondition(value, e);
      }

      this.dispatchValidity(inputElement, isValid);
    };

    validateValue();
    listenOn.forEach((event) =>
      inputElement.addEventListener(event, validateValue)
    );
  }

  private static startInputTextValidity(inputElement: HTMLInputElement) {
    this.checkValue(inputElement, (value) => value.length > 1);
  }

  private static startInputEmailValidity(inputElement: HTMLInputElement) {
    this.checkValue(inputElement, (value) => validateEmail(value) === true);
  }

  private static startInputGoogleAutocompleteAddressValidity(
    inputElement: HTMLInputElement
  ) {
    this.checkValue(
      inputElement,
      (value, e) => {
        const valueExists = !!value;
        const { isAutocompleteValid } = (e as CustomEvent)?.detail ?? {};

        return (isAutocompleteValid && valueExists) ?? false;
      },
      ['place_changed']
    );
  }
}
